import React, { FC, useMemo } from 'react';

import {
	EvFilters,
	EvSection,
	EvSpinner,
	IFilterDefinition,
	IFilterValues,
	OptionType,
	TABLE_FILTERS_SR_SUMMARY,
	TFilterOptions
} from '@evinced-private/ui-common';

import omit from 'lodash-es/omit';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { flattenObject } from 'src/common/helpers/ObjectsHelper';

import { useEffectiveFilters } from '../../helpers/FilterHelpers';

interface BaseFiltersProps {
	filters: IFilterOptions;
	onFilterChange: (filters: IFilterOptions) => void;
	isLoading?: boolean;
	filterDefinitions?: IFilterDefinition[];
	filterOptions?: Record<string, OptionType[]>;
	ariaLabel: string;
	skipLinkId: string;
	className?: string;
	hasTextFilter?: boolean;
}

export const BaseFilters: FC<BaseFiltersProps> = ({
	filters,
	onFilterChange,
	isLoading = false,
	filterDefinitions,
	filterOptions: customFilterOptions,
	ariaLabel,
	skipLinkId,
	className = 'base-filters',
	hasTextFilter = false
}) => {
	const effectiveFilters = useEffectiveFilters(filters, filterDefinitions);

	const initialValues = useMemo((): IFilterValues => {
		if (hasTextFilter) {
			const selectValues: TFilterOptions = omit(effectiveFilters, 'searchValue') as TFilterOptions;

			return {
				text: { searchValue: effectiveFilters?.searchValue },
				select: selectValues
			};
		}

		const selectValues: TFilterOptions = { ...(effectiveFilters as TFilterOptions) };

		return {
			text: {},
			select: selectValues
		};
	}, [effectiveFilters, hasTextFilter]);

	const filterOptions = useMemo(() => {
		if (customFilterOptions) {
			return customFilterOptions;
		}

		return {};
	}, [customFilterOptions]);

	const handleApply = (appliedFilters: IFilterValues): void => {
		const flatFilters = flattenObject<IFilterOptions>(appliedFilters);
		onFilterChange(flatFilters);
	};

	const handleReset = (): void => {
		onFilterChange({});
	};

	return (
		<EvSection
			ariaLabel={ariaLabel}
			ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
			ariaLive="polite"
			skipLinkId={skipLinkId}
			className={`${className}-section`}
		>
			{!isLoading ? (
				<EvFilters
					className={className}
					onApply={handleApply}
					onReset={handleReset}
					initialValues={initialValues}
					filterOptions={filterOptions}
					filterDefinitions={filterDefinitions}
					isFilterOptionsLoaded={!isLoading}
					isInitialValuesLoaded={!isLoading}
				/>
			) : (
				<EvSpinner />
			)}
		</EvSection>
	);
};
